import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Calendar, Loader2 } from 'lucide-react';
import moment from 'moment';
import { ActivityType, createActivity, updateActivity, CreateActivityData } from '../services';
import { getApiUrl, apiClient } from '../utils/api';
import { useToast } from './Toast';
import { DateInput } from './DateInput';
import { TimeInput } from './TimeInput';
import { isMinor as isMinorFromBirthDate } from '@digitaltattoo/shared-utils';

const APPOINTMENT_TYPES = [
  'CONSULTATION',
  'TATTOO_SESSION',
  'PIERCING',
  'TOUCH_UP',
  'REMOVAL',
  'AFTERCARE',
] as const;

type AppointmentType = typeof APPOINTMENT_TYPES[number];

const normalizeAppointmentType = (value?: string): AppointmentType =>
  APPOINTMENT_TYPES.includes(value as AppointmentType) ? (value as AppointmentType) : 'CONSULTATION';

interface CalendarItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onToast?: (message: string, type?: 'success' | 'warning' | 'error' | 'info') => void;
  type: 'activity' | 'appointment';
  calendarType?: 'practitioner' | 'tattoo' | 'piercer' | 'employee';
  initialData?: {
    start: Date;
    end: Date;
    activityTypeId?: string;
    employeeActivityTypeId?: string;
    appointmentType?: string;
    activityId?: string;
    appointmentId?: string;
    shopId?: string;
    practitionerId?: string | null;
    employeeId?: string | null;
    clientId?: string;
    title?: string;
    description?: string;
    isAllDay?: boolean;
  };
  shops: Array<{ id: string; name: string }>;
  practitioners: Array<{ id: string; fullName: string; shopIds?: string[] }>;
  activityTypes: ActivityType[];
  employeeActivityTypes?: ActivityType[];
  selectedShopId?: string;
  allowedAppointmentTypes?: readonly AppointmentType[];
}

interface CalendarClient {
  id: string;
  firstName: string;
  lastName: string;
  isMinor: boolean | null;
  dateOfBirth?: string | null;
  isAnonymous?: boolean;
  anonymousCode?: string | null;

  legalGuardian?: {
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    relationshipToMinor?: string;
  } | null;
}

function getCalendarClientLabel(client: Pick<CalendarClient, 'firstName' | 'lastName' | 'isAnonymous' | 'anonymousCode'>): string {
  const firstName = (client.firstName || '').trim();
  const lastName = (client.lastName || '').trim();
  const code = (client.anonymousCode || '').trim();
  const isAnonymous = Boolean(client.isAnonymous) || Boolean(code);
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  if (isAnonymous) {
    const label = fullName || firstName || 'Client anonyme';
    return code ? `${label} (${code})` : label;
  }

  return fullName || (code ? `Client Inconnu (${code})` : 'Client Inconnu');
}


export function CalendarItemModal({
  isOpen,
  onClose,
  onSave,
  onToast,
  type,
  calendarType,
  initialData,
  shops,
  practitioners,
  activityTypes,
  employeeActivityTypes,
  selectedShopId,
  allowedAppointmentTypes,
}: CalendarItemModalProps) {
  const { t } = useTranslation();
  const { showToast, ToastComponent } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Form state
  const isEditingItem = !!initialData?.activityId || !!initialData?.appointmentId;
  const initialShopId = initialData?.shopId ?? (isEditingItem ? '' : (selectedShopId || ''));
  const [shopId, setShopId] = useState(initialShopId);
  const [practitionerId, setPractitionerId] = useState(initialData?.practitionerId || '');
  const [employeeId, setEmployeeId] = useState(initialData?.employeeId || '');
  const [activityTypeId, setActivityTypeId] = useState(initialData?.activityTypeId || '');
  const [employeeActivityTypeId, setEmployeeActivityTypeId] = useState(initialData?.employeeActivityTypeId || '');
  const [appointmentType, setAppointmentType] = useState<AppointmentType>(
    normalizeAppointmentType(initialData?.appointmentType)
  );
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isAllDay, setIsAllDay] = useState(initialData?.isAllDay || false);
  const [clientId, setClientId] = useState(initialData?.clientId || '');
  const [clients, setClients] = useState<CalendarClient[]>([]);
  const [shopPractitioners, setShopPractitioners] = useState<Array<{ id: string; fullName: string }>>([]);
  const [loadingShopPractitioners, setLoadingShopPractitioners] = useState(false);
  const [shopPractitionerLoadError, setShopPractitionerLoadError] = useState(false);

  const ownerLabel =
    calendarType === 'employee'
      ? t('planning.calendar.modal.employee') || 'Employé'
      : t('planning.calendar.modal.practitioner') || 'Praticien';
  const ownerRequiredMessage =
    calendarType === 'employee'
      ? t('planning.calendar.modal.errors.employeeRequired') || 'Employee is required'
      : t('planning.calendar.modal.errors.practitionerRequired') || 'Practitioner is required';
  const activityOwnerId = calendarType === 'employee' ? employeeId : practitionerId;
  const setActivityOwnerId = calendarType === 'employee' ? setEmployeeId : setPractitionerId;

  // Filter practitioners by selected shop
  const availablePractitioners = (() => {
    if (type === 'activity') {
      const base = practitioners.filter((p) => p.id !== 'UNASSIGNED');
      if (calendarType === 'employee' && shopId) {
        return base.filter((p) => Array.isArray(p.shopIds) && p.shopIds.includes(shopId));
      }
      return base;
    }

    const base = shopId ? shopPractitioners : practitioners;

    if (!isEditingItem || !practitionerId) {
      return base;
    }

    const isAlreadyPresent = base.some((p) => p.id === practitionerId);
    if (isAlreadyPresent) {
      return base;
    }

    const fallbackPractitioner = practitioners.find((p) => p.id === practitionerId);
    if (!fallbackPractitioner) {
      return base;
    }

    return [...base, { id: fallbackPractitioner.id, fullName: fallbackPractitioner.fullName }];
  })();

  // Initialize form from initialData
  useEffect(() => {
    if (initialData && isOpen) {
      // Ensure dates are valid Date objects
      let startMoment: moment.Moment;
      let endMoment: moment.Moment;
      
      if (initialData.start instanceof Date) {
        startMoment = moment(initialData.start);
      } else {
        startMoment = moment(new Date(initialData.start));
      }
      
      if (initialData.end instanceof Date) {
        endMoment = moment(initialData.end);
      } else {
        endMoment = moment(new Date(initialData.end));
      }
      
      // Validate dates
      if (!startMoment.isValid() || !endMoment.isValid()) {
        console.error('Invalid dates in initialData:', { start: initialData.start, end: initialData.end });
        // Use current date/time as fallback
        startMoment = moment();
        endMoment = moment().add(1, 'hour');
      }
      
      setStartDate(startMoment.format('YYYY-MM-DD'));
      setStartTime(startMoment.format('HH:mm'));
      setEndDate(endMoment.format('YYYY-MM-DD'));
      setEndTime(endMoment.format('HH:mm'));

      const nextShopId = initialData.shopId ?? (isEditingItem ? '' : (selectedShopId || ''));
      setShopId(nextShopId);
      setPractitionerId(initialData.practitionerId || '');
      setEmployeeId(initialData.employeeId || '');
      setClientId(initialData.clientId || '');
      setDescription(initialData.description || '');
      setIsAllDay(initialData.isAllDay || false);
      
      if (initialData.activityTypeId) {
        setActivityTypeId(initialData.activityTypeId);
      }
      if (initialData.employeeActivityTypeId) {
        setEmployeeActivityTypeId(initialData.employeeActivityTypeId);
      }

      const resolvedActivityType = activityTypes.find((at) => at.id === initialData.activityTypeId);
      const resolvedEmployeeType = (employeeActivityTypes || []).find(
        (at) => at.id === initialData.employeeActivityTypeId
      );
      const resolvedTitle = resolvedEmployeeType?.name || resolvedActivityType?.name;
      if (resolvedTitle) {
        setTitle(initialData.title || resolvedTitle);
      }
      
      if (initialData.appointmentType) {
      setAppointmentType(normalizeAppointmentType(initialData.appointmentType));
      }

      if (initialData.title && !initialData.activityTypeId && !initialData.employeeActivityTypeId) {
        setTitle(initialData.title);
      }
    }
  }, [initialData, isOpen, activityTypes, employeeActivityTypes, calendarType, selectedShopId]);

  useEffect(() => {
    if (type !== 'activity') {
      return;
    }
    if (activityTypeId) {
      return;
    }
    const defaultType = activityTypes.find((at) => at.isDefault);
    if (defaultType) {
      setActivityTypeId(defaultType.id);
      if (!title) {
        setTitle(defaultType.name);
      }
    }
  }, [activityTypes, activityTypeId, title, type]);

  useEffect(() => {
    if (type !== 'appointment') {
      return;
    }
    if (!allowedAppointmentTypes || allowedAppointmentTypes.length === 0) {
      return;
    }
    if (!allowedAppointmentTypes.includes(appointmentType)) {
      setAppointmentType(allowedAppointmentTypes[0]);
    }
  }, [allowedAppointmentTypes, appointmentType, type]);

  // Load clients when shop changes
  useEffect(() => {
    if (shopId && type === 'appointment') {
      loadClients();
    }
  }, [shopId, type]);

  useEffect(() => {
    if (!shopId) {
      setShopPractitioners([]);
      setShopPractitionerLoadError(false);
      return;
    }
    loadShopPractitioners();
  }, [shopId]);

  useEffect(() => {
    if (type === 'activity') {
      return;
    }
    if (isEditingItem) {
      return;
    }
    if (!shopId) {
      return;
    }
    if (!practitionerId) {
      return;
    }
    const isInShop = shopPractitioners.some((p) => p.id === practitionerId);
    if (!isInShop) {
      setPractitionerId('');
    }
  }, [shopPractitioners, practitionerId, shopId, type, isEditingItem]);

  const loadClients = async () => {
    try {
      const response = await apiClient(`/api/admin/clients?shopId=${shopId}&limit=100`);

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setClients(
            data.data.clients.map((c: any) => ({
              id: c.id,
              firstName: c.firstName,
              lastName: c.lastName,
              isAnonymous:
                c.isAnonymous === true
                || c.isAnonymous === 1
                || c.isAnonymous === '1'
                || c.isAnonymous === 't'
                || c.isAnonymous === 'true',
              anonymousCode: c.anonymousCode || null,

              isMinor: c.dateOfBirth
                ? isMinorFromBirthDate(c.dateOfBirth)
                : c.isMinor === true
                  || c.isMinor === 1
                  || c.isMinor === '1'
                  || c.isMinor === 'true'
                  || c.isMinor === 't'
                  ? true
                  : c.isMinor === false
                    || c.isMinor === 0
                    || c.isMinor === '0'
                    || c.isMinor === 'false'
                    || c.isMinor === 'f'
                    ? false
                    : null,

              dateOfBirth: c.dateOfBirth || null,

              legalGuardian: c.legalGuardian
                ? {
                    firstName: c.legalGuardian.firstName || '',
                    lastName: c.legalGuardian.lastName || '',
                    phone: c.legalGuardian.phone || '',
                    email: c.legalGuardian.email || '',
                    relationshipToMinor:
                      c.legalGuardian.relationshipToMinor || '',
                  }
                : null,
            }))
          );
        }
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const loadShopPractitioners = async () => {
    setLoadingShopPractitioners(true);
    setShopPractitionerLoadError(false);
    try {
      const response = await apiClient(`/api/planning/practitioners/shop/${shopId}`);
      if (!response.ok) {
        setShopPractitioners([]);
        setShopPractitionerLoadError(true);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setShopPractitioners(
          (data.data?.practitioners || []).map((p: any) => ({
            id: p.id,
            fullName: `${p.firstName} ${p.lastName}`.trim(),
          }))
        );
      } else {
        setShopPractitioners([]);
        setShopPractitionerLoadError(true);
      }
    } catch (error) {
      console.error('Failed to load shop practitioners:', error);
      setShopPractitioners([]);
      setShopPractitionerLoadError(true);
    } finally {
      setLoadingShopPractitioners(false);
    }
  };

  const handleAppointmentStartDateChange = (newStartDate: string) => {
    if (!newStartDate) {
      setStartDate(newStartDate);
      return;
    }

    const oldStart = moment(`${startDate}T${startTime}`);
    const oldEnd = moment(`${endDate}T${endTime}`);
    const newStart = moment(`${newStartDate}T${startTime}`);

    setStartDate(newStartDate);

    if (
      type !== 'appointment' ||
      !oldStart.isValid() ||
      !oldEnd.isValid() ||
      !newStart.isValid() ||
      !oldEnd.isAfter(oldStart)
    ) {
      return;
    }

    const durationMinutes = oldEnd.diff(oldStart, 'minutes');
    const newEnd = newStart.clone().add(durationMinutes, 'minutes');

    setEndDate(newEnd.format('YYYY-MM-DD'));
    setEndTime(newEnd.format('HH:mm'));
  };

  const handleAppointmentStartTimeChange = (newStartTime: string) => {
    if (!newStartTime) {
      setStartTime(newStartTime);
      return;
    }

    const oldStart = moment(`${startDate}T${startTime}`);
    const oldEnd = moment(`${endDate}T${endTime}`);
    const newStart = moment(`${startDate}T${newStartTime}`);

    setStartTime(newStartTime);

    if (
      type !== 'appointment' ||
      !oldStart.isValid() ||
      !oldEnd.isValid() ||
      !newStart.isValid() ||
      !oldEnd.isAfter(oldStart)
    ) {
      return;
    }

    const durationMinutes = oldEnd.diff(oldStart, 'minutes');
    const newEnd = newStart.clone().add(durationMinutes, 'minutes');

    setEndDate(newEnd.format('YYYY-MM-DD'));
    setEndTime(newEnd.format('HH:mm'));
  };

  const handleSave = async () => {
    setError(null);
    setLoading(true);
    const notify = onToast ?? showToast;

    try {
      if (!shopId && type === 'appointment') {
        throw new Error(t('planning.calendar.modal.errors.shopRequired') || 'Shop is required');
      }

      if (initialData?.shopId && shopId && shopId !== initialData.shopId) {
        notify(
          t('planning.calendar.modal.shopMoveNotice') ||
            'This activity will be moved to another shop.',
          'warning'
        );
      }

      // For appointments: allow piercings to be created unassigned (assignment later)
      if (type === 'activity' && !activityOwnerId) {
        throw new Error(ownerRequiredMessage);
      }

      if (type === 'activity') {
        if (!activityTypeId) {
          throw new Error(t('planning.calendar.modal.errors.activityTypeRequired') || 'Activity type is required');
        }

        if (!title.trim()) {
          throw new Error(t('planning.calendar.modal.errors.titleRequired') || 'Title is required');
        }

        // Build start and end datetimes
        const startDateTime = isAllDay
          ? moment(startDate).startOf('day').toISOString()
          : moment(`${startDate}T${startTime}`).toISOString();
        const endDateTime = isAllDay
          ? moment(endDate).endOf('day').toISOString()
          : moment(`${endDate}T${endTime}`).toISOString();

        const activityData: CreateActivityData = {
          practitionerId: calendarType === 'employee' ? undefined : practitionerId,
          employeeId: calendarType === 'employee' ? employeeId : undefined,
          employeeActivityTypeId: calendarType === 'employee' ? employeeActivityTypeId || undefined : undefined,
          shopId: shopId ? shopId : null,
          activityTypeId,
          title: title.trim(),
          description: description.trim() || undefined,
          startDateTime,
          endDateTime,
          isAllDay,
        };

        if (initialData?.activityId) {
          // Update existing activity
          await updateActivity(initialData.activityId, activityData);
        } else {
          // Create new activity
          await createActivity(activityData);
        }
      } else {
        // Appointment creation
        if (!clientId) {
          throw new Error(t('planning.calendar.modal.errors.clientRequired') || 'Client is required');
        }

        if (allowedAppointmentTypes && allowedAppointmentTypes.length > 0) {
          if (!allowedAppointmentTypes.includes(appointmentType)) {
            throw new Error(t('planning.calendar.modal.errors.invalidAppointmentType') || 'Appointment type not allowed for this calendar.');
          }
        }

        const startDateTime = moment(`${startDate}T${startTime}`).toISOString();
        const endDateTime = moment(`${endDate}T${endTime}`).toISOString();

        const appointmentData = {
          clientId,
          practitionerId: practitionerId || null,
          shopId,
          type: appointmentType,
          scheduledAt: startDateTime,
          estimatedEndTime: endDateTime,
          description: description.trim() || undefined,
        };

        const isEditingAppointment = !!initialData?.appointmentId;

        const url = isEditingAppointment
          ? `/api/admin/appointments/${initialData.appointmentId}`
          : '/api/admin/appointments';

        const response = await apiClient(url, {
          method: isEditingAppointment ? 'PUT' : 'POST',
          body: JSON.stringify(appointmentData),
        });

        const data = await response.json().catch(() => null);

        if (!response.ok || !data?.success) {
          throw new Error(
            data?.message || data?.error || 'Failed to save appointment'
          );
        }

        // Send the confirmation email only when a new appointment is created.
        // Email delivery failure must not cancel an appointment that was already created.
        if (!isEditingAppointment) {
          const appointmentId =
            data?.data?.id || data?.data?.appointment?.id;

          if (appointmentId) {
            try {
              const confirmationResponse = await apiClient(
                `/api/admin/appointments/${appointmentId}/resend-confirmation-email`,
                { method: 'POST' },
              );

              const confirmationData = await confirmationResponse
                .json()
                .catch(() => null);

              if (!confirmationResponse.ok || !confirmationData?.success) {
                console.warn(
                  'Appointment created, but confirmation email failed:',
                  confirmationData?.message || confirmationData?.error,
                );
                notify(
                  confirmationData?.message ||
                    confirmationData?.error ||
                    'Rendez-vous créé, mais l’email de confirmation n’a pas pu être envoyé.',
                  'warning',
                );
              }
            } catch (confirmationError) {
              console.warn(
                'Appointment created, but confirmation email failed:',
                confirmationError,
              );
              notify(
                'Rendez-vous créé, mais l’email de confirmation n’a pas pu être envoyé.',
                'warning',
              );
            }
          } else {
            console.warn(
              'Appointment created, but no appointment id was returned for confirmation email.',
            );
          }
        }
      }

      onSave();
      onClose();
      notify(
        t('planning.calendar.modal.saveSuccess') || 'Saved successfully.',
        'success'
      );
    } catch (err: any) {
      const message = err.message || 'Failed to save';
      setError(message);
      notify(message, 'error', 7000);
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  const selectedClient =
    clients.find((client) => client.id === clientId) || null;
  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/60 p-4 ${loading ? 'cursor-wait' : ''}`}
      onClick={loading ? undefined : onClose}
    >
      {!onToast && <ToastComponent />}
      <div
        className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-primary-500 to-accent-600 px-6 py-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20">
                <Calendar className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-white/80">
                  {type === 'activity'
                    ? t('planning.calendar.modal.activity') || 'Activité'
                    : t('planning.calendar.modal.appointment') || 'Rendez-vous'}
                </p>
                <h3 className="text-lg font-semibold">
                  {initialData?.activityId || initialData?.appointmentId
                    ? t('planning.calendar.modal.editTitle') || 'Modifier'
                    : t('planning.calendar.modal.createTitle') || 'Créer'}{' '}
                  {type === 'activity'
                    ? t('planning.calendar.modal.activity') || 'une Activité'
                    : t('planning.calendar.modal.appointment') || 'un Rendez-vous'}
                </h3>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
              disabled={loading}
              aria-label={t('planning.calendar.modal.close') || 'Close'}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-6">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Date/Time Range */}
          <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-semibold text-gray-700">
              {t('planning.calendar.modal.dateTime') || 'Date & Time'}
            </h4>
            {type === 'activity' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isAllDay"
                  checked={isAllDay}
                  onChange={(e) => setIsAllDay(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={loading}
                />
                <label htmlFor="isAllDay" className="ml-2 text-sm text-gray-700">
                  {t('planning.calendar.modal.allDay') || 'Journée entière'}
                </label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('planning.calendar.modal.startDate') || 'Date de début'} *
                </label>
               <DateInput
                value={startDate}
                onChange={handleAppointmentStartDateChange}
                className="input-field"
                required
                disabled={loading}
              />
              </div>
              {!isAllDay && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('planning.calendar.modal.startTime') || 'Heure de début'} *
                  </label>
                  <TimeInput
  value={startTime}
  onChange={handleAppointmentStartTimeChange}
  className="input-field"
  required
  disabled={loading}
/>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('planning.calendar.modal.endDate') || 'Date de fin'} *
                </label>
                <DateInput
                  value={endDate}
                  onChange={setEndDate}
                  className="input-field"
                  required
                  disabled={loading}
                />
              </div>
              {!isAllDay && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('planning.calendar.modal.endTime') || 'Heure de fin'} *
                  </label>
                 <TimeInput
  value={endTime}
  onChange={setEndTime}
  className="input-field"
  required
  disabled={loading}
/>
                </div>
              )}
            </div>
          </div>

          {/* Shop Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('planning.calendar.modal.shop') || 'Boutique'} {type === 'appointment' ? '*' : ''}
            </label>
            <select
              value={shopId}
              onChange={(e) => {
                setShopId(e.target.value);
                setClientId('');
                if (type === 'appointment') {
                  setPractitionerId('');
                }
              }}
              className="input-field"
              required
              disabled={loading || (!!selectedShopId && !initialData?.activityId && !initialData?.appointmentId)}
            >
              <option value="">
                {type === 'activity'
                  ? (t('planning.calendar.modal.allShops') || 'All shops')
                  : (t('planning.calendar.modal.selectShop') || 'Sélectionner une boutique')}
              </option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
            {type === 'activity' && (
              <p className="mt-1 text-xs text-gray-500">
                {t('planning.calendar.modal.activityAllShopsHint') || 'Leave empty to apply this activity to all shops.'}
              </p>
            )}
          </div>

          {/* Practitioner Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {ownerLabel} {type === 'activity' ? '*' : ''}
            </label>
            <select
              value={type === 'activity' ? activityOwnerId : practitionerId}
              onChange={(e) => {
                if (type === 'activity') {
                  setActivityOwnerId(e.target.value);
                } else {
                  setPractitionerId(e.target.value);
                }
              }}
              className="input-field"
              required={type === 'activity'}
              disabled={loading || loadingShopPractitioners || shopPractitionerLoadError}
            >
              <option value="">
                {type === 'activity'
                  ? (calendarType === 'employee'
                      ? (t('planning.calendar.modal.selectEmployee') || 'Sélectionner un employé')
                      : (t('planning.calendar.modal.selectPractitioner') || 'Sélectionner un praticien'))
                  : (t('planning.calendar.modal.unassignedLater') || 'Non assigné (assignation plus tard)')}
              </option>
              {loadingShopPractitioners && (
                <option value="" disabled>
                  {t('planning.calendar.modal.loadingPractitioners') || 'Chargement des praticiens...'}
                </option>
              )}
              {availablePractitioners.map((practitioner) => (
                <option key={practitioner.id} value={practitioner.id}>
                  {practitioner.fullName}
                </option>
              ))}
            </select>
            {shopPractitionerLoadError && (
              <p className="mt-1 text-xs text-red-600">
                {t('planning.calendar.modal.loadPractitionersError') || 'Unable to load practitioners for this shop.'}
              </p>
            )}
            {!loadingShopPractitioners && shopId && availablePractitioners.length === 0 && !shopPractitionerLoadError && (
              <p className="mt-1 text-xs text-gray-500">
                {t('planning.calendar.modal.noPractitionersForShop') || 'No practitioners linked to this shop.'}
              </p>
            )}
            {type === 'appointment' && (
              <p className="mt-1 text-xs text-gray-500">
                {t('planning.calendar.modal.practitionerOptional') || 'Le praticien peut être assigné plus tard.'}
              </p>
            )}
          </div>

          {/* Activity Types (for activities) */}
          {type === 'activity' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('planning.calendar.modal.activityType') || 'Type d\'activité'} *
                </label>
                <select
                  value={activityTypeId}
                  onChange={(e) => {
                    setActivityTypeId(e.target.value);
                    const activityType = activityTypes.find((at) => at.id === e.target.value);
                    if (activityType && !title) {
                      setTitle(activityType.name);
                    }
                  }}
                  className="input-field"
                  required
                  disabled={loading}
                >
                  <option value="">
                    {t('planning.calendar.modal.selectActivityType') || 'Sélectionner un type'}
                  </option>
                  {activityTypes.map((at) => (
                    <option key={at.id} value={at.id}>
                      {at.name}
                    </option>
                  ))}
                </select>
              </div>

              {calendarType === 'employee' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {(t('planning.calendar.modal.employeeActivityType') || 'Type d\'activité employé')} (optionnel)
                  </label>
                  <select
                    value={employeeActivityTypeId}
                    onChange={(e) => {
                      setEmployeeActivityTypeId(e.target.value);
                      const employeeType = (employeeActivityTypes || []).find((at) => at.id === e.target.value);
                      if (employeeType && !title) {
                        setTitle(employeeType.name);
                      }
                    }}
                    className="input-field"
                    disabled={loading}
                  >
                    <option value="">
                      {t('planning.calendar.modal.selectEmployeeActivityType') || 'Sélectionner un type employé'}
                    </option>
                    {(employeeActivityTypes || []).map((at) => (
                      <option key={at.id} value={at.id}>
                        {at.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* Appointment Type (for appointments) */}
          {type === 'appointment' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('planning.calendar.modal.appointmentType') || 'Type de rendez-vous'} *
              </label>
              <select
                value={appointmentType}
                onChange={(e) => setAppointmentType(normalizeAppointmentType(e.target.value))}
                className="input-field"
                required
                disabled={loading}
              >
                {(!allowedAppointmentTypes || allowedAppointmentTypes.includes('CONSULTATION')) && (
                  <option value="CONSULTATION">{t('planning.calendar.modal.types.consultation') || 'Consultation'}</option>
                )}
                {(!allowedAppointmentTypes || allowedAppointmentTypes.includes('TATTOO_SESSION')) && (
                  <option value="TATTOO_SESSION">{t('planning.calendar.modal.types.tattoo') || 'Tatouage'}</option>
                )}
                {(!allowedAppointmentTypes || allowedAppointmentTypes.includes('PIERCING')) && (
                  <option value="PIERCING">{t('planning.calendar.modal.types.piercing') || 'Piercing'}</option>
                )}
                {(!allowedAppointmentTypes || allowedAppointmentTypes.includes('TOUCH_UP')) && (
                  <option value="TOUCH_UP">{t('planning.calendar.modal.types.touchUp') || 'Retouche'}</option>
                )}
                {(!allowedAppointmentTypes || allowedAppointmentTypes.includes('REMOVAL')) && (
                  <option value="REMOVAL">{t('planning.calendar.modal.types.removal') || 'Suppression'}</option>
                )}
                {(!allowedAppointmentTypes || allowedAppointmentTypes.includes('AFTERCARE')) && (
                  <option value="AFTERCARE">{t('planning.calendar.modal.types.aftercare') || 'Suivi'}</option>
                )}
              </select>
            </div>
          )}

          {type === 'appointment' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('planning.calendar.modal.client') || 'Client'} *
              </label>

              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className={`input-field ${
                  selectedClient?.isMinor ? 'border-amber-300 bg-amber-50' : ''
                }`}
                required
                disabled={loading || !shopId}
              >
                <option value="">
                  {t('planning.calendar.modal.selectClient') || 'Sélectionner un client'}
                </option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {getCalendarClientLabel(client)}
                    {client.isMinor ? ' — MINEUR' : ''}
                  </option>
                ))}
              </select>

    {/* Informations client mineur */}
    {selectedClient?.isMinor && (
      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 border border-amber-200">
            Mineur
          </span>

          <span className="text-sm font-semibold text-amber-900">
            {getCalendarClientLabel(selectedClient)}
          </span>
        </div>

        {selectedClient.legalGuardian ? (
          <div className="mt-3 border-t border-amber-200 pt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
              Tuteur légal
            </p>

            <p className="mt-1 text-sm font-semibold text-gray-900">
              {selectedClient.legalGuardian.firstName}{' '}
              {selectedClient.legalGuardian.lastName}
            </p>

            <div className="mt-1 space-y-1 text-xs text-gray-600">
              {selectedClient.legalGuardian.phone && (
                <p>
                  Téléphone :{' '}
                  <span className="font-medium text-gray-800">
                    {selectedClient.legalGuardian.phone}
                  </span>
                </p>
              )}

              {selectedClient.legalGuardian.email && (
                <p>
                  Email :{' '}
                  <span className="font-medium text-gray-800">
                    {selectedClient.legalGuardian.email}
                  </span>
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <p className="text-xs font-semibold text-red-700">
              Aucun tuteur légal renseigné
            </p>
          </div>
        )}
      </div>
    )}
  </div>
)}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('planning.calendar.modal.title') || 'Titre'} {type === 'activity' && '*'}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-field"
              placeholder={t('planning.calendar.modal.titlePlaceholder') || 'Titre de l\'activité ou du rendez-vous'}
              required={type === 'activity'}
              disabled={loading}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('planning.calendar.modal.description') || 'Description'}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="input-field"
              placeholder={t('planning.calendar.modal.descriptionPlaceholder') || 'Description (optionnel)'}
              disabled={loading}
            />
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading}
          >
            {t('planning.calendar.modal.cancel') || 'Annuler'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('planning.calendar.modal.saving') || 'Enregistrement...'}</span>
              </>
            ) : (
              t('planning.calendar.modal.save') || 'Enregistrer'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
